import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Lock, User } from 'lucide-react';
import { useWS } from '../WSContext';

interface Props {
  onSuccess: (username: string) => void;
}

// ─── Lakers Logo (küçük) ──────────────────────────────────────────────────────
function SmallLogo() {
  return (
    <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="60,4 50,28 70,28" fill="#4A9FE8"/>
      <polygon points="26,18 38,42 52,30" fill="#4A9FE8" opacity="0.8"/>
      <polygon points="94,18 82,42 68,30" fill="#4A9FE8" opacity="0.8"/>
      <circle cx="60" cy="72" r="42" fill="#003087"/>
      <path d="M 20 60 Q 60 44 100 60" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 20 80 Q 60 96 100 80" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 60 30 Q 44 60 60 114" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 60 30 Q 76 60 60 114" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <circle cx="60" cy="72" r="18" fill="#4A9FE8" opacity="0.15"/>
    </svg>
  );
}

export default function LoginOverlay({ onSuccess }: Props) {
  const { subscribe, sendWs, connected } = useWS();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'auth_result') {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setLoading(false);
        if (data.payload?.success) {
          onSuccess(data.payload.username);
        } else if (data.payload?.blocked) {
          const wait = data.payload?.retry_after;
          const waitStr = wait ? ` ${wait < 60 ? `${wait} saniye` : `${Math.ceil(wait / 60)} dakika`} sonra` : '';
          setError(`Çok fazla hatalı giriş —${waitStr} tekrar deneyin.`);
          setPassword('');
        } else {
          setError('Kullanıcı adı veya şifre hatalı');
          setPassword('');
        }
      }
    });
  }, [subscribe, onSuccess]);

  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password || loading || !connected) return;
    setLoading(true);
    setError('');
    sendWs({ type: 'auth', payload: { username: username.trim(), password } });

    // 6 saniye içinde yanıt gelmezse timeout — bağlantı kesilmiş olabilir
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      setError('Sunucudan yanıt alınamadı — tekrar deneyin');
    }, 6000);
  }, [username, password, loading, connected, sendWs]);

  const inp = 'w-full bg-[#0a2560]/60 border border-[#003087] text-white placeholder-blue-600 focus:border-[#4A9FE8] focus:outline-none rounded-lg py-2 text-sm transition-colors';

  return (
    <div className="fixed inset-0 bg-[#030d1f] flex items-center justify-center z-50">
      <div className="bg-[#061530] border border-[#0a2560] rounded-2xl p-8 w-[340px] shadow-2xl">

        {/* Logo & başlık */}
        <div className="flex flex-col items-center mb-7">
          <SmallLogo />
          <h1 className="text-xl font-black tracking-widest text-[#4A9FE8] uppercase mt-3">Raydium</h1>
          <p className="text-xs text-blue-400 mt-0.5">Perps Bot · Giriş</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {/* Kullanıcı adı */}
          <div>
            <label className="text-xs text-blue-400 block mb-1">Kullanıcı Adı</label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500" />
              <input
                type="text"
                value={username}
                onChange={e => { setUsername(e.target.value); setError(''); }}
                className={`${inp} pl-9 pr-3`}
                placeholder="kullanıcı adı"
                autoComplete="username"
                autoFocus
              />
            </div>
          </div>

          {/* Şifre */}
          <div>
            <label className="text-xs text-blue-400 block mb-1">Şifre</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500" />
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                className={`${inp} pl-9 pr-3`}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          {/* Hata */}
          {error && (
            <p className="text-xs text-rose-400 text-center bg-rose-900/20 rounded-lg py-1.5">{error}</p>
          )}

          {/* Gönder */}
          <button
            type="submit"
            disabled={loading || !username || !password || !connected}
            className="w-full py-2.5 mt-1 bg-[#003087] hover:bg-[#0057A8] disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-colors"
          >
            {loading ? 'Giriş yapılıyor...' : !connected ? 'Bağlanıyor...' : 'Giriş Yap'}
          </button>
        </form>
      </div>
    </div>
  );
}
